import { ChevronRight, Lock } from 'lucide-react';
import { toast } from 'sonner';
import type { CategoriaObservacao, NivelAtencao } from '../../api/hooks/useObservacoes';
import {
  CATEGORIAS_SIGILOSAS,
  NIVEL_CONFIG,
  btnPrimary,
  btnSecondary,
  inputStyle,
  labelStyle,
} from './observacoes-professor-shared';
import { OBSERVACAO_EDICAO_DIAS_PROFESSOR } from './observacoes-utils';
import type { ObservacoesProfessorPageContext } from './useObservacoesProfessorPage';

type Props = Pick<
  ObservacoesProfessorPageContext,
  | 'form'
  | 'setForm'
  | 'meusAlunos'
  | 'categoriasDisponiveis'
  | 'temAcessoTotal'
  | 'setShowAlunoPicker'
  | 'setAlunoSearch'
  | 'handleSalvar'
  | 'resetForm'
  | 'editingId'
  | 'criar'
  | 'atualizar'
  | 'loadingAlunos'
> & { hideActions?: boolean };

export function ObservacoesFormFields({
  form,
  setForm,
  meusAlunos,
  categoriasDisponiveis,
  temAcessoTotal,
  setShowAlunoPicker,
  setAlunoSearch,
  handleSalvar,
  resetForm,
  editingId,
  criar,
  atualizar,
  loadingAlunos,
  hideActions = false,
}: Props) {
  return (
    <>
      <div className="obs-form-grid">
        <div className="obs-form-full">
          <label style={labelStyle}>Aluno *</label>
          <button
            type="button"
            onClick={() => {
              setAlunoSearch('');
              setShowAlunoPicker(true);
            }}
            className="obs-form-aluno-btn"
          >
            {(() => {
              if (!form.alunoId) return <span style={{ flex: 1 }}>Selecione o aluno</span>;
              const sel = meusAlunos.find((a) => a.id === form.alunoId);
              if (!sel) return <span style={{ flex: 1 }}>Aluno nao encontrado</span>;
              const iniciais = sel.nome
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0])
                .join('')
                .toUpperCase();
              return (
                <>
                  <span className="obs-form-aluno-avatar">{iniciais}</span>
                  <span className="obs-form-aluno-nome">{sel.nome}</span>
                  <span className="obs-form-aluno-turma">{sel.turma?.nome || ''}</span>
                </>
              );
            })()}
            <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          </button>
        </div>
        <div className="obs-form-full">
          <label style={labelStyle}>Título *</label>
          <input
            value={form.titulo}
            onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            placeholder="Ex: Dificuldade em Matemática"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Categoria</label>
          <select
            value={form.categoria}
            onChange={(e) => {
              const cat = e.target.value as CategoriaObservacao;
              const forcarPrivado = CATEGORIAS_SIGILOSAS.includes(cat);
              setForm({ ...form, categoria: cat, ...(forcarPrivado ? { privado: true } : {}) });
            }}
            style={inputStyle}
          >
            {categoriasDisponiveis.map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
          {!temAcessoTotal && (
            <div className="obs-form-hint-lock">
              <Lock size={10} />
              <span>Saúde, Familiar e Social: apenas diretoria</span>
            </div>
          )}
        </div>
        <div>
          <label style={labelStyle}>Nível de Atenção</label>
          <select
            value={form.nivelAtencao}
            onChange={(e) => setForm({ ...form, nivelAtencao: e.target.value as NivelAtencao })}
            style={inputStyle}
          >
            {Object.entries(NIVEL_CONFIG).map(([k, v]) => (
              <option key={k} value={k}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
        <div className="obs-form-full">
          <label style={labelStyle}>Descrição *</label>
          <textarea
            value={form.descricao}
            onChange={(e) => setForm({ ...form, descricao: e.target.value })}
            placeholder="Registro pedagógico para acompanhamento interno..."
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          />
        </div>
        <div className="obs-form-full">
          <label className="obs-form-check">
            <input
              type="checkbox"
              checked={form.privado}
              onChange={(e) => {
                if (CATEGORIAS_SIGILOSAS.includes(form.categoria) && !e.target.checked) {
                  toast.error('Categorias sensíveis são obrigatoriamente privadas');
                  return;
                }
                setForm({ ...form, privado: e.target.checked });
              }}
            />
            Observação privada (visível apenas para você)
          </label>
          <p className="obs-form-hint-edicao">
            Você pode editar ou remover registros manuais por até {OBSERVACAO_EDICAO_DIAS_PROFESSOR} dias após a data de
            referência.
          </p>
        </div>
      </div>
      {!hideActions && (
        <div className="obs-form-actions">
          <button type="button" onClick={resetForm} style={btnSecondary}>
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={
              !form.titulo ||
              !form.descricao ||
              !form.alunoId ||
              criar.isPending ||
              atualizar?.isPending ||
              loadingAlunos
            }
            style={{
              ...btnPrimary,
              ...(!form.titulo || !form.descricao || !form.alunoId ? { opacity: 0.5 } : {}),
            }}
          >
            {criar.isPending || atualizar?.isPending
              ? 'Salvando...'
              : editingId
                ? 'Atualizar'
                : 'Salvar'}
          </button>
        </div>
      )}
    </>
  );
}
